import React, { useMemo, useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  SimpleGrid,
  Text,
  Center,
  Spinner,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { Icon } from 'src/components/atoms/Icon';
import { CustomToast } from 'src/components/atoms/CustomToast';
import { useCustomToast } from 'src/hooks/useCustomToast';
import { useAppContext } from 'src/contexts/AppContext';
import {
  useModelsQuery,
  useDeleteModelMutation,
  ModelListItem,
} from 'src/api/models';
import { collapseActivePairs } from 'src/features/Settings/utils/modelPairs';
import { ModelCard } from '../molecules/ModelCard';
import { ViewModelDrawer } from '../molecules/ViewModelDrawer';
import { DeleteModelDialog } from '../molecules/DeleteModelDialog';
import { CreateModelPage } from './CreateModelPage';
import { EditModelPage } from './EditModelPage';

export type ModelsView =
  | { type: 'list' }
  | { type: 'create' }
  | { type: 'edit'; modelId: string };

type ModelsTabProps = {
  view: ModelsView;
  onViewChange: (view: ModelsView) => void;
};

export const ModelsTab: React.FC<ModelsTabProps> = ({ view, onViewChange }) => {
  const { currentCustomer } = useAppContext();
  const customerId = currentCustomer?.customerName ?? '';
  const { toastState, showSuccess, showError, hideToast } = useCustomToast();

  const [isViewDrawerOpen, setIsViewDrawerOpen] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [modelToDelete, setModelToDelete] = useState<ModelListItem | null>(
    null,
  );

  const { data: models = [], isLoading, isError } = useModelsQuery(customerId);
  const deleteModelMutation = useDeleteModelMutation(customerId);

  // A Quick/Full pair whose halves are both Active shows only the Full card;
  // in any other status combination both stay visible.
  const visibleModels = useMemo(() => collapseActivePairs(models), [models]);

  // Long-term models built on the model being deleted (either pair half):
  // the backend cascade-deletes them, so the dialog must warn about it.
  const dependentLtNames = useMemo(() => {
    if (!modelToDelete) return [];
    const pairIds = models
      .filter(
        (m) =>
          m.modelName === modelToDelete.modelName &&
          m.createdAt === modelToDelete.createdAt,
      )
      .map((m) => m.id);
    const names = models
      .filter(
        (m) =>
          m.modelType === 'lt' &&
          m.parentModelId != null &&
          pairIds.includes(m.parentModelId),
      )
      .map((m) => m.modelName);
    return Array.from(new Set(names));
  }, [models, modelToDelete]);

  const handleShowList = () => onViewChange({ type: 'list' });
  const handleShowCreate = () => onViewChange({ type: 'create' });
  const handleShowEdit = (modelId: string) =>
    onViewChange({ type: 'edit', modelId });

  const handleQuickView = (model: ModelListItem) => {
    setSelectedModelId(model.id);
    setIsViewDrawerOpen(true);
  };

  const handleEdit = (model: ModelListItem) => handleShowEdit(model.id);

  const handleDeleteClick = (model: ModelListItem) => {
    setModelToDelete(model);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!modelToDelete) return;
    try {
      await deleteModelMutation.mutateAsync({ modelId: modelToDelete.id });
      showSuccess('Model deleted', 'The model has been removed successfully.');
      setIsDeleteDialogOpen(false);
      setModelToDelete(null);
      if (selectedModelId === modelToDelete.id) {
        setIsViewDrawerOpen(false);
        setSelectedModelId(null);
      }
    } catch (error) {
      // The backend refuses deleting a running/pending model with a
      // user-facing reason — show it instead of the generic text.
      const serverMessage = (
        error as { response?: { data?: { message?: string } } }
      )?.response?.data?.message;
      showError(
        'Delete failed',
        serverMessage ?? 'Unable to delete the model. Please try again.',
      );
    }
  };

  const handleCloseViewDrawer = () => {
    setIsViewDrawerOpen(false);
    setSelectedModelId(null);
  };

  if (view.type === 'create') {
    return (
      <>
        <CreateModelPage onComplete={handleShowList} />
        <CustomToast
          isOpen={toastState.isOpen}
          title={toastState.title}
          description={toastState.description}
          type={toastState.type}
          onClose={hideToast}
        />
      </>
    );
  }

  if (view.type === 'edit') {
    return (
      <>
        <EditModelPage modelId={view.modelId} onComplete={handleShowList} />
        <CustomToast
          isOpen={toastState.isOpen}
          title={toastState.title}
          description={toastState.description}
          type={toastState.type}
          onClose={hideToast}
        />
      </>
    );
  }

  return (
    <>
      <Box
        bg="surface.panel"
        borderRadius="16px"
        border="1px solid"
        borderColor="surface.border"
        w="100%"
        p={6}>
        <Flex
          justify="space-between"
          align={{ base: 'flex-start', md: 'center' }}
          mb={6}
          direction={{ base: 'column', md: 'row' }}
          gap={{ base: 3, md: 0 }}>
          <Text
            fontFamily="marketingPage"
            fontWeight="bold"
            fontSize="lg"
            color="content.title">
            MODELS
          </Text>
          <Button
            variant="primary"
            buttonName="add-new-model"
            onClick={handleShowCreate}
            maxW={{ base: '100%', md: 'auto' }}
            overflow="hidden">
            <Icon
              iconName="plus"
              boxSize={4}
              mr={2}
              color="white"
              flexShrink={0}
            />
            <Text isTruncated>Add new model</Text>
          </Button>
        </Flex>

        {isError ? (
          <Center py={16}>
            <Text color="red.500">
              We were unable to load models. Please try again later.
            </Text>
          </Center>
        ) : isLoading ? (
          <Center py={16}>
            <Spinner color="icon.green" size="lg" />
          </Center>
        ) : visibleModels.length === 0 ? (
          <Center py={16}>
            <Text color="content.muted">
              No models yet. Create one to get started.
            </Text>
          </Center>
        ) : (
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
            {visibleModels.map((model) => (
              <ModelCard
                key={model.id}
                model={model}
                onQuickView={handleQuickView}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
              />
            ))}
          </SimpleGrid>
        )}
      </Box>

      <ViewModelDrawer
        isOpen={isViewDrawerOpen}
        onClose={handleCloseViewDrawer}
        modelId={selectedModelId}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
      />

      <DeleteModelDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        modelName={modelToDelete?.modelName ?? ''}
        isLoading={deleteModelMutation.isLoading}
        dependentModelNames={dependentLtNames}
      />

      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        onClose={hideToast}
      />
    </>
  );
};
